import { ref } from "vue";

/**
 * Moteur de recherche rapide réutilisable (autocomplete / typeahead).
 *
 * - Debounce : on n'interroge le backend qu'après une pause de frappe.
 * - Garde anti-obsolescence : si plusieurs requêtes partent, seule la
 *   réponse de la DERNIÈRE frappe est appliquée (numéro de séquence) —
 *   évite qu'une réponse lente écrase une réponse récente.
 * - `minChars` : pas d'appel en dessous du seuil (économise le réseau).
 *
 * Le `fetcher` reçoit le terme et renvoie la liste de résultats déjà
 * mise en forme par l'appelant — `useQuickSearch` ne connaît aucune
 * ressource précise : il sert aussi bien aux clients qu'aux colis,
 * billets, etc.
 *
 * Exemple :
 *   const { results, loading, searched, search } = useQuickSearch(
 *     async (term) => (await api().customers.search(`?...&search=${term}`)).data,
 *   );
 */
export function useQuickSearch<T = any>(
	fetcher: (term: string) => Promise<T[]>,
	options?: { debounceMs?: number; minChars?: number },
) {
	const debounceMs = options?.debounceMs ?? 300;
	const minChars = options?.minChars ?? 2;

	const term = ref("");
	const results = ref<T[]>([]) as { value: T[] };
	const loading = ref(false);
	/** Passe à `true` une fois qu'une recherche a abouti (≠ état initial). */
	const searched = ref(false);

	let timer: ReturnType<typeof setTimeout> | null = null;
	let seq = 0;

	const run = async (q: string) => {
		const mySeq = ++seq;
		loading.value = true;
		try {
			const data = await fetcher(q);
			if (mySeq !== seq) return; // réponse obsolète → ignorée
			results.value = Array.isArray(data) ? data : [];
			searched.value = true;
		} catch (e) {
			if (mySeq !== seq) return;
			results.value = [];
			searched.value = true;
		} finally {
			if (mySeq === seq) loading.value = false;
		}
	};

	const search = (q: string) => {
		term.value = q ?? "";
		if (timer) clearTimeout(timer);

		const trimmed = (q ?? "").trim();
		if (trimmed.length < minChars) {
			seq++; // annule toute requête en vol
			results.value = [];
			searched.value = false;
			loading.value = false;
			return;
		}
		timer = setTimeout(() => run(trimmed), debounceMs);
	};

	const reset = () => {
		if (timer) clearTimeout(timer);
		seq++;
		term.value = "";
		results.value = [];
		loading.value = false;
		searched.value = false;
	};

	return { term, results, loading, searched, search, reset };
}

export default useQuickSearch;
